import { MoreHoriz } from '@mui/icons-material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import {
  Breadcrumbs,
  IconButton,
  Link,
  Menu,
  MenuItem,
  styled,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { Link as RouterLink } from 'react-router';
import useBreadcrumbContext from './useBreadcrumbContext';

export interface Breadcrumb {
  text: string;
  path: string;
}

export type BreadcrumbTrail = Array<Breadcrumb>;

export interface BreadcrumbNavigation {
  title: string;
  crumbs: BreadcrumbTrail;
}

const StyledSpan = styled('span')();
const TextForBigScreens: React.FC<React.PropsWithChildren> = ({ children }) => (
  <StyledSpan sx={{ display: { xs: 'none', lg: 'inline' } }}>{children}</StyledSpan>
);

const Breadcrumb: React.FC<React.PropsWithChildren<BreadcrumbNavigation>> = ({ title, crumbs }) => {
  const theme = useTheme();
  const { translation } = useTranslation();
  const isWideScreen = useMediaQuery(theme.breakpoints.up('md'));
  const isTruncated = !isWideScreen && crumbs.length > 0;

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleTruncatedOpen = (event: React.MouseEvent<HTMLButtonElement> | null) => {
    if (event) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleTruncateClose = () => {
    setAnchorEl(null);
  };

  return (
    <div role="presentation">
      <Menu anchorEl={anchorEl} open={open} onClose={handleTruncateClose}>
        {crumbs.map((crumb) => (
          <MenuItem component={RouterLink} to={crumb.path} onClick={handleTruncateClose} key={crumb.path}>
            {crumb.text}
          </MenuItem>
        ))}
      </Menu>
      <Breadcrumbs aria-label={translation.breadcrumb}>
        <Link
          component={RouterLink}
          to="/"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center' }}
          color="inherit"
        >
          <HomeOutlinedIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          <TextForBigScreens>{translation.home}</TextForBigScreens>
        </Link>
        {isTruncated ? (
          <IconButton size="small" onClick={handleTruncatedOpen}>
            <MoreHoriz />
          </IconButton>
        ) : (
          crumbs.map((crumb) => (
            <Link
              component={RouterLink}
              to={crumb.path}
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              color="inherit"
              key={crumb.path}
            >
              {crumb.text}
            </Link>
          ))
        )}
        <Typography color="textPrimary" sx={{ display: 'flex', alignItems: 'center' }}>
          {title}
        </Typography>
      </Breadcrumbs>
    </div>
  );
};

export const BreadcrumbFromContext = () => {
  const { breadcrumb } = useBreadcrumbContext();

  if (breadcrumb) {
    return <Breadcrumb title={breadcrumb.title} crumbs={breadcrumb.crumbs} />;
  }
};

export const BreadcrumbMeta: React.FC<React.PropsWithChildren<{ breadcrumb: BreadcrumbNavigation | null }>> = ({
  breadcrumb
}) => {
  const { setBreadcrumb } = useBreadcrumbContext();

  React.useLayoutEffect(() => {
    setBreadcrumb(breadcrumb);
  }, [breadcrumb]);

  return undefined;
};

export default Breadcrumb;
